import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle, Trash2, Pencil, FileDown, X, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import DisputeDialog from "./DisputeDialog";
import { generateDisputePdf } from "@/lib/disputePdf";

export const THIRD_PARTY_LABELS = {
  client: "Client",
  supplier: "Fournisseur",
  subcontractor: "Sous-traitant",
  employee: "Salarié",
  administration: "Administration",
  insurer: "Assureur",
  banker: "Banquier",
  interim_agency: "Agence intérim",
  other: "Autre",
};

export const STATUS_LABELS = {
  to_handle: "À traiter",
  negotiating: "En négociation",
  litigation: "Au contentieux",
  waiting: "En attente",
  closed: "Clos",
};

export const STATUS_COLORS = {
  to_handle: "bg-blue-100 text-blue-800 border-blue-200",
  negotiating: "bg-amber-100 text-amber-800 border-amber-200",
  litigation: "bg-red-100 text-red-800 border-red-200",
  waiting: "bg-slate-100 text-slate-700 border-slate-200",
  closed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export const SEVERITY_LABELS = {
  low: "Faible",
  medium: "Moyen",
  high: "Fort",
  critical: "Critique",
};

const SEVERITY_RANK = { low: 0, medium: 1, high: 2, critical: 3 };

export const SEVERITY_COLORS = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-yellow-100 text-yellow-800",
  high: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

export const CATEGORY_LABELS = {
  paiement: "Paiement",
  qualite: "Qualité",
  delais: "Délais",
  contractuel: "Contractuel",
  rh: "RH",
  fiscal: "Fiscal",
  autre: "Autre",
};

// Quick-filter presets (chips above the table)
const QUICK_FILTERS = [
  { key: "overdue", label: "En retard", icon: "⚠️" },
  { key: "due_7d", label: "Échéance ≤ 7j" },
  { key: "with_amount", label: "Avec enjeu €" },
  { key: "blacklist", label: "Blacklist" },
  { key: "critical", label: "Critiques" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const isoPlusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export const DisputesTab = () => {
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentDispute, setCurrentDispute] = useState(null);

  // Filters synced to URL
  const [params, setParams] = useSearchParams();
  const filterStatus = params.get("status") || "open";
  const filterSeverity = params.get("sev") || "all";
  const filterCategory = params.get("cat") || "all";
  const filterType = params.get("type") || "all";
  const filterDue = params.get("due") || "";
  const search = params.get("q") || "";
  const quick = params.get("quick") || ""; // overdue | due_7d | with_amount | blacklist | critical
  const sortBy = params.get("sortBy") || "due"; // due | severity | amount | created
  const sortDir = params.get("sortDir") || "asc";

  const setP = useCallback((updates) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k); else next.set(k, v);
    });
    setParams(next, { replace: true });
  }, [params, setParams]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const apiParams = {};
      if (filterStatus === "open") apiParams.include_closed = false;
      else if (filterStatus !== "all") apiParams.status = filterStatus;
      if (filterSeverity !== "all") apiParams.severity = filterSeverity;
      if (filterDue) apiParams.due_before = filterDue;
      const { data } = await api.getDisputes(apiParams);
      setDisputes(data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity, filterDue]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tIso = todayIso();
  const in7d = isoPlusDays(7);

  // Apply client-side filters (category, type, search, quick presets)
  const filtered = useMemo(() => {
    const term = (search || "").trim().toLowerCase();
    return disputes.filter(d => {
      if (filterCategory !== "all" && d.category !== filterCategory) return false;
      if (filterType !== "all" && d.third_party_type !== filterType) return false;
      if (term) {
        const matches =
          (d.third_party_name || "").toLowerCase().includes(term) ||
          (d.reason || "").toLowerCase().includes(term) ||
          (d.next_action || "").toLowerCase().includes(term);
        if (!matches) return false;
      }
      if (quick === "overdue") {
        if (!(d.next_action_due_date && d.next_action_due_date < tIso && d.status !== "closed")) return false;
      } else if (quick === "due_7d") {
        if (!(d.next_action_due_date && d.next_action_due_date <= in7d && d.status !== "closed")) return false;
      } else if (quick === "with_amount") {
        if (!(d.amount_at_stake && d.amount_at_stake > 0)) return false;
      } else if (quick === "blacklist") {
        if (!d.blacklisted) return false;
      } else if (quick === "critical") {
        if (d.severity !== "critical") return false;
      }
      return true;
    });
  }, [disputes, search, filterCategory, filterType, quick, tIso, in7d]);

  const sorted = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case "severity":
          av = SEVERITY_RANK[a.severity] ?? 0; bv = SEVERITY_RANK[b.severity] ?? 0; break;
        case "amount":
          av = a.amount_at_stake || 0; bv = b.amount_at_stake || 0; break;
        case "created":
          av = a.created_at || ""; bv = b.created_at || ""; break;
        case "third_party":
          av = (a.third_party_name || "").toLowerCase(); bv = (b.third_party_name || "").toLowerCase(); break;
        case "due":
        default:
          // Sort: nulls last
          av = a.next_action_due_date || "9999-12-31";
          bv = b.next_action_due_date || "9999-12-31";
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setP({ sortDir: sortDir === "asc" ? "desc" : "asc" });
    else setP({ sortBy: col, sortDir: col === "amount" || col === "severity" ? "desc" : "asc" });
  };

  const sortIcon = (col) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const openCreate = () => { setCurrentDispute(null); setDialogOpen(true); };
  const openEdit = (d) => { setCurrentDispute(d); setDialogOpen(true); };

  const handleDelete = async (d) => {
    if (!window.confirm(`Supprimer définitivement le litige avec ${d.third_party_name} ?`)) return;
    try {
      await api.deleteDispute(d.id);
      toast.success("Litige supprimé");
      fetchData();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const handleExport = async (d) => {
    try {
      const { data } = await api.getDispute(d.id);
      generateDisputePdf(data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur export"));
    }
  };

  const resetFilters = () => setP({
    status: null, sev: null, cat: null, type: null, due: null, q: null, quick: null, sortBy: null, sortDir: null,
  });

  const hasActiveFilter =
    filterStatus !== "open" || filterSeverity !== "all" || filterCategory !== "all" ||
    filterType !== "all" || filterDue || search || quick;

  const tomorrowIso = isoPlusDays(1);

  // Counters for quick filters (computed on the FETCHED set, before client filter)
  const counters = useMemo(() => ({
    overdue: disputes.filter(d => d.next_action_due_date && d.next_action_due_date < tIso && d.status !== "closed").length,
    due_7d: disputes.filter(d => d.next_action_due_date && d.next_action_due_date <= in7d && d.status !== "closed").length,
    with_amount: disputes.filter(d => d.amount_at_stake && d.amount_at_stake > 0).length,
    blacklist: disputes.filter(d => d.blacklisted).length,
    critical: disputes.filter(d => d.severity === "critical").length,
  }), [disputes, tIso, in7d]);

  return (
    <div className="space-y-4" data-testid="disputes-tab">
      <Card>
        <CardHeader className="flex flex-col gap-3 pb-3">
          <div className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Litiges <Badge variant="outline">{sorted.length}{sorted.length !== disputes.length ? ` / ${disputes.length}` : ""}</Badge>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="Rechercher (tiers, motif, action)…"
                value={search}
                onChange={(e) => setP({ q: e.target.value })}
                className="w-72"
                data-testid="disputes-search"
              />
              <Button onClick={openCreate} data-testid="disputes-new-btn">
                <Plus className="h-4 w-4 mr-1" /> Nouveau litige
              </Button>
            </div>
          </div>

          {/* Filter row */}
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterStatus} onValueChange={(v) => setP({ status: v === "open" ? null : v })}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="disputes-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Ouverts uniquement</SelectItem>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSeverity} onValueChange={(v) => setP({ sev: v === "all" ? null : v })}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="disputes-filter-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toute gravité</SelectItem>
                {Object.entries(SEVERITY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={(v) => setP({ cat: v === "all" ? null : v })}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="disputes-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toute catégorie</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={(v) => setP({ type: v === "all" ? null : v })}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="disputes-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tout type de tiers</SelectItem>
                {Object.entries(THIRD_PARTY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input
                type="date"
                value={filterDue}
                onChange={(e) => setP({ due: e.target.value })}
                className="w-40 h-8 text-xs"
                placeholder="Échéance ≤"
                data-testid="disputes-filter-due"
              />
              {filterDue && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setP({ due: null })} title="Effacer">
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            {hasActiveFilter && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 text-xs" data-testid="disputes-reset-filters">
                <X className="h-3 w-3 mr-1" /> Réinitialiser
              </Button>
            )}
          </div>

          {/* Quick filter chips */}
          <div className="flex flex-wrap gap-1.5">
            {QUICK_FILTERS.map(qf => {
              const active = quick === qf.key;
              const count = counters[qf.key] || 0;
              return (
                <button
                  key={qf.key}
                  type="button"
                  onClick={() => setP({ quick: active ? null : qf.key })}
                  className={`text-[11px] font-medium px-2 py-1 rounded-full border transition-all duration-200 ease-out ${
                    active
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50"
                  } ${count === 0 ? "opacity-50" : ""}`}
                  data-testid={`disputes-quick-${qf.key}`}
                  disabled={count === 0 && !active}
                >
                  {qf.icon ? `${qf.icon} ` : ""}{qf.label}
                  <span className={`ml-1 ${active ? "text-zinc-300" : "text-zinc-400"}`}>· {count}</span>
                </button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              Aucun litige ne correspond aux filtres sélectionnés.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button onClick={() => toggleSort("third_party")} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                      Tiers {sortIcon("third_party")}
                    </button>
                  </TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Motif</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>
                    <button onClick={() => toggleSort("severity")} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                      Gravité {sortIcon("severity")}
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button onClick={() => toggleSort("amount")} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                      Enjeu {sortIcon("amount")}
                    </button>
                  </TableHead>
                  <TableHead>Prochaine action</TableHead>
                  <TableHead>
                    <button onClick={() => toggleSort("due")} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                      Échéance {sortIcon("due")}
                    </button>
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(d => {
                  const due = d.next_action_due_date;
                  const overdue = due && due < tIso && d.status !== "closed";
                  const dueSoon = due && (due === tIso || due === tomorrowIso) && d.status !== "closed";
                  const orderCount = (() => {
                    const ids = new Set();
                    if (Array.isArray(d.order_ids)) d.order_ids.forEach(o => o && ids.add(o));
                    if (d.order_id) ids.add(d.order_id);
                    return ids.size;
                  })();
                  return (
                    <TableRow key={d.id} data-testid={`dispute-row-${d.id}`}>
                      <TableCell className="font-medium">
                        {d.third_party_name}
                        {d.blacklisted && <Badge className="ml-1 bg-red-600 text-white border-red-700">Blacklist</Badge>}
                        {orderCount > 0 && (
                          <Badge
                            variant="outline"
                            className="ml-1 bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]"
                            title={`${orderCount} commande(s) rattachée(s)`}
                            data-testid={`dispute-orders-count-${d.id}`}
                          >
                            {orderCount} cmd
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {THIRD_PARTY_LABELS[d.third_party_type] || d.third_party_type}
                          {d.third_party_type === "other" && d.third_party_other_label ? ` • ${d.third_party_other_label}` : ""}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[280px] truncate" title={d.reason}>
                        {d.reason}
                        {d.category && d.category !== "autre" && (
                          <span className="ml-1 text-[10px] text-zinc-400">· {CATEGORY_LABELS[d.category] || d.category}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[d.status]} border`}>{STATUS_LABELS[d.status]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={SEVERITY_COLORS[d.severity]}>{SEVERITY_LABELS[d.severity]}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {d.amount_at_stake ? formatCurrency(d.amount_at_stake) : "—"}
                        {d.provision_made && <span className="ml-1 text-xs text-green-700" title="Provision comptable effectuée">✓</span>}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={d.next_action || ""}>{d.next_action || "—"}</TableCell>
                      <TableCell className={overdue ? "text-red-600 font-bold" : dueSoon ? "text-amber-700 font-semibold" : ""}>
                        {due ? formatDate(due) : "—"}
                        {overdue && <span className="ml-1 text-xs">⚠ retard</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleExport(d)} data-testid={`dispute-export-${d.id}`} title="Exporter en PDF">
                          <FileDown className="h-4 w-4 text-blue-700" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`dispute-edit-${d.id}`} title="Modifier">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(d)} data-testid={`dispute-delete-${d.id}`} title="Supprimer">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DisputeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        dispute={currentDispute}
        onSaved={() => fetchData()}
      />
    </div>
  );
};

export default DisputesTab;
